/**
 * Generates an array of symbols representing a rating "system", given a specific rating value and maximum (default is 5).
 * The array can contain full, half, and empty custom symbols, as required to represent the rating.
 *
 * @param symbol - The symbols to use for full, half, and empty ratings.
 * @param full - The symbol used for full ratings.
 * @param half - The symbol used for half ratings.
 * @param empty - The symbol used for empty ratings.
 * @param rating - The rating value.
 * @param maximum - The maximum number of symbols in the rating "system" (default is 5).
 * @returns An array of symbols representing the rating, including full, half, and empty symbols.
 *
 * @remarks This function is used to generate the rating array,
 * if you don't want to have a maximum number of stars, use Math.round(rating) as the maximum.
 * like the following code (same code as in src/helpers/calculate-stars.ts):
 * ```js
 *  const starts = generateRatingArray({
 *      symbol: {
 *          full: star,
 *          half: halfStar,
 *          empty: '',
 *      },
 *      rating: value,
 *      maximum: Math.round(value),
 *  });
 * ```
 */ export const generateRatingArray = ({ symbol: { full , half , empty  } , rating , maximum =5  })=>{
    // Calculate the number of full symbols to display, ensuring it doesn't exceed the maximum value.
    const fullSymbolQuantityToFill = Math.min(Math.floor(rating), maximum);
    // Check if the rating has a fractional part.
    const hasFractionalPart = rating % 1 !== 0;
    // Check if it's possible to add a half symbol without exceeding the maximum value.
    const canAddHalfSymbol = fullSymbolQuantityToFill < maximum;
    // Determine if a half symbol should be added.
    const hasHalfSymbol = hasFractionalPart && canAddHalfSymbol;
    // Calculate the number of empty symbols needed to complete the maximum required number of symbols.
    const emptySymbolsQuantityToFill = maximum - fullSymbolQuantityToFill - (hasHalfSymbol ? 1 : 0);
    // Create an array of full symbols
    const fullSymbols = Array(fullSymbolQuantityToFill).fill(full);
    // Create an array containing a half symbol, if applicable
    const halfSymbol = hasHalfSymbol ? [
        half
    ] : [];
    // Create an array of empty symbols
    const emptySymbols = Array(emptySymbolsQuantityToFill).fill(empty);
    // Combine and return the full, half, and empty symbols arrays
    return [
        ...fullSymbols,
        ...halfSymbol,
        ...emptySymbols
    ];
};

//# sourceMappingURL=rating.js.map